import React, { useState } from "react";
import {
  Table,
  Segment,
  Button,
  Header,
  Message,
  Grid,
  Dimmer,
  Loader,
} from "semantic-ui-react";
import { Form, Formik } from "formik";
import { normalizeId } from "../../../../../../formatting/inventoryIds";
import axios from "../../../../../../apis/dvdlocator";

const ReviewStep = ({
  inventories,
  supplyUnits,
  mergedInventory,
  onStepComplete,
  onPrevious,
}) => {
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const initialValues = {
    confirmChanges: false,
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    setIsProcessing(true);

    try {
      const upcsToUpdate = supplyUnits.filter(
        (unit) => unit.inventory_id !== mergedInventory.inventory_id
      );

      const linkIdRequests = upcsToUpdate.map((unit) => ({
        upc: unit.upc,
        inventory_id: mergedInventory.inventory_id,
      }));

      // Step 2: Update UPCs
      await axios.post(`/supply/link-inventory`, {
        supplyUnits: linkIdRequests,
      });

      // Step 3: Update the merged inventory record
      const editRequest = {
        title: mergedInventory.inventory_title,
        shelf: mergedInventory.shelf,
        inv: mergedInventory.inv,
      };

      await axios.put(
        `/inventory/${mergedInventory.inventory_id}`,
        editRequest
      );

      // Step 4: Delete the old inventory records
      const inventoryToDelete = inventories.filter(
        (inv) => inv.inventory_id !== mergedInventory.inventory_id
      );

      for (const inv of inventoryToDelete) {
        await axios.delete(`/inventory/${inv.inventory_id}`);
      }

      // Everything succeeded
      onStepComplete();
    } catch (err) {
      console.error("Error during merge process:", err);
      setError(
        err.response?.data?.message ||
          "An error occurred while processing the merge. Please try again."
      );
    } finally {
      setIsProcessing(false);
      setSubmitting(false);
    }
  };

  // Get the total count of all supply units
  const totalSupplyUnits = supplyUnits.length;

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ isSubmitting, submitForm }) => (
        <Form>
          <Segment>
            {isProcessing && (
              <Dimmer active inverted>
                <Loader>Processing Merge</Loader>
              </Dimmer>
            )}

            <Header as="h4">Review Merge Changes</Header>

            {error && (
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error}</p>
              </Message>
            )}

            <p>
              Please review the details of the merged inventory record. All
              supply units will be assigned to this single inventory.
            </p>

            {/* Merged Inventory Section */}
            <Segment
              secondary
              style={{ marginBottom: "20px", marginTop: "20px" }}
            >
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Header as="h4">Inventory Title</Header>
                    <p>{mergedInventory.inventory_title}</p>

                    <Header as="h4">Shelf</Header>
                    <p>{mergedInventory.shelf}</p>

                    <Header as="h4">Inventory Value</Header>
                    <p>{mergedInventory.inv}</p>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Header as="h3">
                      Target Inventory ID:{" "}
                      {normalizeId(mergedInventory.inventory_id)}
                    </Header>
                    <p>Component #: {mergedInventory.part_number}</p>
                    <p>{totalSupplyUnits} total supply units will be merged</p>
                    <Message info>
                      <Message.Header>
                        Merging {inventories.length} Inventory Records
                      </Message.Header>
                      <p>
                        All supply units from the selected inventories will be
                        consolidated into this single inventory record.
                      </p>
                      <p>
                        Inventory record(s){" "}
                        <span style={{ fontWeight: "bold" }}>
                          {inventories
                            .filter(
                              (inv) =>
                                inv.inventory_id !==
                                mergedInventory.inventory_id
                            )
                            .map((inv) => normalizeId(inv.inventory_id))
                            .join(",")}
                        </span>{" "}
                        will be deleted. 🧹
                      </p>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>

            {/* Supply Units Table */}
            <Header as="h4">Supply Units to be Merged</Header>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>UPC</Table.HeaderCell>
                  <Table.HeaderCell>Title</Table.HeaderCell>
                  <Table.HeaderCell>Original Inventory ID</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {supplyUnits.map((unit, index) => {
                  // Find the original inventory this unit belonged to
                  const sourceInventory = inventories.find(
                    (inv) => inv.inventory_id === unit.inventory_id
                  );

                  return (
                    <Table.Row key={`${unit.upc}-${index}`}>
                      <Table.Cell>{unit.upc}</Table.Cell>
                      <Table.Cell>{unit.title}</Table.Cell>
                      <Table.Cell>
                        {sourceInventory
                          ? normalizeId(sourceInventory.inventory_id)
                          : "Unknown"}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>

            {/* Action buttons */}
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button
                type="button"
                onClick={onPrevious}
                disabled={isProcessing}
              >
                Back
              </Button>
              <Button
                type="submit"
                primary
                disabled={isSubmitting || isProcessing}
                loading={isProcessing}
              >
                Confirm Merge
              </Button>
            </div>
          </Segment>
        </Form>
      )}
    </Formik>
  );
};

export default ReviewStep;
